import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCategoryBySlug, CATEGORIES } from '../Categories/constant';
import {
  getProductsByCategory,
  getCategoryDescription,
  CATEGORIES_PRODUCTS,
} from './products';
import Header from '../Header/Header.jsx';
import Footer from '../Footer/Footer.jsx';
import {
  PageContainer,
  Container,
  BackButton,
  PageHeader,
  HeaderLeft,
  CategoryTitle,
  ResultCount,
  Breadcrumbs,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  ContentWrapper,
  Sidebar,
  SidebarText,
  CategoryList,
  CategoryListItem,
  CategoryListWrapper,
  ProductsGrid,
  ProductCard,
  ProductImageWrapper,
  ProductImage,
  ProductOverlay,
  ProductArrow,
  ProductName,
} from './ProductCategory.styled';

const ProductCategory = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const category = getCategoryBySlug(slug);
  const products = getProductsByCategory(slug || '');
  const productCount = products.length;

  // Scroll to top instantly when category changes (no animation)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) {
    return (
      <PageContainer>
        <Header />
        <Container>
          <CategoryTitle>Category not found</CategoryTitle>
          <BackButton onClick={() => navigate('/')}>
            Go back home
          </BackButton>
        </Container>
        <Footer />
      </PageContainer>
    );
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <PageContainer>
      <Header />
      <Container
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <PageHeader variants={headerVariants}>
          <HeaderLeft>
            <Breadcrumbs>
              <BreadcrumbLink to="/products">PRODUCTS</BreadcrumbLink>
              <BreadcrumbSeparator>|</BreadcrumbSeparator>
              <BreadcrumbCurrent>{category.name}</BreadcrumbCurrent>
            </Breadcrumbs>
            <CategoryTitle
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {category.name}
            </CategoryTitle>
            <ResultCount
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Showing all {productCount} results
            </ResultCount>
          </HeaderLeft>
        </PageHeader>

        <ContentWrapper>
          <Sidebar variants={itemVariants}>
            <SidebarText variants={itemVariants}>
              {getCategoryDescription(slug)}
            </SidebarText>
            {(() => {
              // Only show the current category in sidebar
              const currentCategory = CATEGORIES.find(
                (cat) => cat.slug === slug
              );
              if (!currentCategory) return null;

              const categoryData = CATEGORIES_PRODUCTS[slug];
              if (!categoryData) return null;

              return (
                <CategoryListWrapper>
                  <CategoryList as={motion.ul} variants={itemVariants}>
                    {categoryData.products.map((product, productIndex) => (
                      <CategoryListItem
                        key={product.id}
                        variants={itemVariants}
                        custom={productIndex + 1}
                      >
                        <Link
                          to={`/products/${product.slug}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          {product.name}
                        </Link>
                      </CategoryListItem>
                    ))}
                  </CategoryList>
                </CategoryListWrapper>
              );
            })()}
          </Sidebar>

          <ProductsGrid
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {products.length > 0 ? (
              products.map((product, index) => (
                <ProductCard
                  key={product.id}
                  as={Link}
                  to={`/products/${product.slug}`}
                  variants={itemVariants}
                  custom={index}
                >
                  <ProductImageWrapper>
                    <ProductImage
                      src={product.image}
                      alt={product.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <ProductOverlay
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ProductArrow
                        initial={{ x: -20, opacity: 0 }}
                        whileHover={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        →
                      </ProductArrow>
                    </ProductOverlay>
                  </ProductImageWrapper>
                  <ProductName>{product.name}</ProductName>
                </ProductCard>
              ))
            ) : (
              <p>No products found in this category.</p>
            )}
          </ProductsGrid>
        </ContentWrapper>
      </Container>
      <Footer />
    </PageContainer>
  );
};

export default ProductCategory;
